// EP-111 T111-9 (D9/D11) — the co-owner sidebar page. Reuses the
// Customers-list-style kit (PageHeader + Card + status Tabs + card grid);
// see docs/ep-111-condominium-property-type.md D11 for the ASCII mockup.
export default {
  title: 'Co-owner',
  description: 'Manage the co-owners (juristic-person owners) of your condominium units.',

  labels: {
    first_name: 'First Name',
    last_name: 'Last Name',
    phone: 'Phone',
    email: 'Email',
    id_card_number: 'ID Card',
    tax_id: 'Tax ID',
    address: 'Address',
    line_id: 'LINE ID',
    property: 'Property',
    unit: 'Unit',
    started_at: 'Since',
    ended_at: 'End date',
    usage_status: 'Usage status',
    // EP-111 amendment (payment info) — bank + PromptPay for routing the
    // CAF invoice directly to the co-owner.
    payment_info: 'Payment information',
    bank_name: 'Bank',
    bank_account_number: 'Account number',
    bank_account_holder: 'Account holder',
    promptpay_target: 'PromptPay (phone / national ID / bill-payment code)',
  },

  placeholders: {
    search: 'Search unit no. / co-owner name...',
    first_name: 'First name',
    last_name: 'Last name',
    phone: '0812345678',
    email: 'email@example.com',
    bank_name: 'Bangkok Bank',
    bank_account_number: '123-4-56789-0',
    bank_account_holder: 'Co-owner full name',
    promptpay_target: '0812345678',
  },

  // Usage status (D10/D15) — the co-owner's self-declared intent. 'rented' is
  // DERIVED server-side (an active tenant RentalLease exists on the unit) and
  // is never a value the co-owner sets directly.
  status: {
    owner_occupied: 'Owner-occupied',
    available_for_rent: 'Available for rent',
    rented: 'Rented',
    for_sale: 'For sale',
  },

  // D15 read-only-when-derived: when effective_usage_status === 'rented' the
  // form's <select> is hidden and replaced with this lock+explanation block.
  // The status cannot be cleared from the form — only by ending the active
  // tenant RentalLease (out of scope for this form).
  derived_status: {
    label: 'Current status',
    // i18n note: the English text matches the spec verbatim. The Thai label
    // surfaces the "คำนวณจากสัญญาเช่า" derivation hint the operator needs to
    // understand why the select is gone.
    explanation: 'Rented (derived from active rental lease)',
  },

  tabs: {
    all: 'All',
    owner_occupied: 'Owner-occupied',
    available_for_rent: 'Available for rent',
    rented: 'Rented',
    for_sale: 'For sale',
  },

  sections: {
    recent: 'Recent',
  },

  actions: {
    add_co_owner: 'Add co-owner',
    create_co_owner: 'Create co-owner',
    edit: 'Edit co-owner',
    end: 'End co-owner',
    save_changes: 'Save changes',
    // EP-134 §7 — owner transfer. The transfer page lives on the unit
    // detail; the action label is mirrored here so the list page can link
    // out via the kebab menu and a future cross-surface button stays in
    // one i18n key.
    transfer_ownership: 'Transfer ownership',
  },

  empty: {
    no_results: 'No co-owners found',
  },

  // EP-135 Tier 0 — shared-owner contact directory (standalone page).
  // A read-only roster rendered as a table (one row per active OwnerContract),
  // scoped by property; phone and email are click-to-call / click-to-mail so
  // the operator can drive AGM notice delivery without a separate lookup.
  // The Tier 1/2 governance surfaces will reuse the same roll (via
  // SharedOwnerRoll.for(property, as_of:)) to snapshot at notice/vote date.
  directory: {
    title: 'Co-owner directory',
    description:
      'Contact list of co-owners (juristic-person owners) on each condominium property. For AGM notices and committee elections.',
    column_unit: 'Unit',
    column_name: 'Name',
    column_share_ratio: 'Share',
    column_contact: 'Contact',
    share_ratio_value: '{{value}}%',
    call: 'Call',
    email: 'Email',
    no_phone: 'No phone on file',
    no_email: 'No email on file',
    property_filter_label: 'Property',
    property_filter_all: 'All properties',
    empty_state:
      'No co-owners to list yet. Add one from the Co-owner page — they will appear here automatically.',
  },

  setup_guide: {
    title: 'Welcome to Co-owner',
    description:
      'Co-owners are the juristic-person owners of your condominium units. Add one here to bill them the common area fee (CAF).',
    step_1_title: 'Mark a property as condominium',
    step_1_description: 'On the property Pricing tab, set the property type to condominium.',
    step_2_title: 'Add the co-owner',
    step_2_description: 'Enter the co-owner’s details and pick their unit.',
  },

  messages: {
    created: 'Co-owner created successfully',
    updated: 'Co-owner updated successfully',
    ended: 'Co-owner ended successfully',
    end_title: 'End co-owner',
    end_confirm: 'End co-owner "{{name}}"? Their common area fee billing will stop from the next cycle.',
    identifier_required:
      'Please enter at least a name, phone number, or email so this co-owner can be identified.',
    unit_required: 'Please select a unit.',
    // EP-134 §4 — transfer form. Mirrors the typed 422 codes from
    // OwnerTransferService::TransferError so the surface renders Thai +
    // English consistently. The §9 Q-5 money silence is deliberate — the
    // confirm step says NOTHING about arrears, prepaid CAF, or settlement;
    // those are settled at the land office. The hint about tenant
    // adjustment is the only operator-facing note (§9 Q-2).
    transfer: {
      title: 'Transfer ownership',
      description:
        'Record a condominium ownership transfer. The current owner\'s contract is ended and the buyer\'s begins on the transfer date.',
      transfer_date_label: 'Transfer date',
      transfer_date_help: 'Land-office registration date. Back-dating is allowed; future dates are not.',
      to_section_label: 'Incoming owner',
      to_create_option: 'Create a new co-owner',
      to_pick_option: 'Pick an existing person',
      pick_customer_label: 'Existing person',
      create_success: 'Ownership transferred',
      tenant_warning: 'This unit\'s rental lease needs adjustment — the tenant is not affected by the transfer.',
      committee_warning: 'The outgoing owner holds a committee seat — ending their ownership does not automatically vacate it.',
      errors: {
        buyer_required: 'Either pick an existing person or fill the new co-owner fields.',
        buyer_phone_required: 'Phone is required for the incoming owner — it is their login and OTP channel.',
        transfer_date_required: 'Please pick a transfer date.',
        transfer_date_in_future: 'Transfer date cannot be in the future.',
        transfer_date_before_outgoing_started_at: 'Transfer date cannot be earlier than the current owner\'s start date.',
        no_active_owner_contract: 'This unit has no active co-owner to transfer.',
        owner_transfer_requires_condominium_property: 'Co-owners can only be transferred on a condominium property.',
        buyer_equals_seller: 'The incoming owner must be different from the current owner.',
        customer_not_found: 'That person does not belong to this account.',
      },
    },
    // EP-134 §2b — ownership history block on unit detail. Past contracts
    // appear in reverse-chronological order; the ⓘ tooltip marks rows that
    // came in via a transfer record (linked to the next contract).
    history: {
      title: 'Ownership history',
      until_now: 'to present',
      until: 'until {{date}}',
      from: 'from {{date}}',
      transferred_from_tooltip: 'This ownership was transferred to the next co-owner.',
    },
  },
}
